import { Link } from 'react-router-dom';
import Header from '../components/Header';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to ProductTrace ("we," "our," or "us"). This Privacy Policy explains how we collect, 
                use, disclose, and safeguard your information when you use our product analysis platform 
                and related services (the "Service").
              </p>
              <p className="text-gray-700">
                By using our Service, you agree to the collection and use of information in accordance 
                with this Privacy Policy. If you do not agree with our policies and practices, 
                do not use our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">
                When you create an account through Google OAuth, we collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your Google account email address</li>
                <li>Your full name as provided by Google</li>
                <li>Your profile picture (if available)</li>
                <li>A unique Google identifier</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Usage Information</h3>
              <p className="text-gray-700 mb-4">
                We automatically collect information about how you use our Service:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Product searches and analysis requests</li>
                <li>Pages visited and features used</li>
                <li>Time spent on the platform</li>
                <li>Device information and browser type</li>
                <li>IP address and general location</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.3 Feedback and Communications</h3>
              <p className="text-gray-700">
                When you provide feedback or contact us, we collect the content of your 
                communications and any additional information you choose to provide.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Provide, maintain, and improve our Service</li>
                <li>Authenticate your account and ensure security</li>
                <li>Process your product analysis requests</li>
                <li>Personalize your experience on the platform</li>
                <li>Respond to your feedback and support requests</li>
                <li>Send important updates about our Service</li>
                <li>Analyze usage patterns to improve our features</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties, 
                except in the following circumstances:
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Service Providers</h3>
              <p className="text-gray-700 mb-4">
                We may share information with trusted third-party service providers who assist us in:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Cloud hosting and data storage</li>
                <li>Analytics and performance monitoring</li>
                <li>Customer support services</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 Legal Requirements</h3>
              <p className="text-gray-700 mb-4">
                We may disclose your information if required by law or in response to:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Valid legal processes or government requests</li>
                <li>Protection of our rights, property, or safety</li>
                <li>Protection of our users or the public</li>
                <li>Investigation of potential violations of our Terms of Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement appropriate technical and organizational security measures to protect 
                your personal information against unauthorized access, alteration, disclosure, or destruction:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication using Google OAuth</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and monitoring</li>
                <li>Secure cloud infrastructure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700">
                We retain your personal information for as long as necessary to provide our Service 
                and fulfill the purposes outlined in this Privacy Policy. We will delete or anonymize 
                your information when it is no longer needed, unless we are required to retain it 
                for legal, regulatory, or legitimate business purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for data processing where applicable</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar tracking technologies to enhance your experience:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Authentication cookies:</strong> To keep you logged in</li>
                <li><strong>Preference cookies:</strong> To remember your settings</li>
                <li><strong>Analytics cookies:</strong> To understand how you use our Service</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You can control cookies through your browser settings, but disabling certain 
                cookies may affect the functionality of our Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services</h2>
              <p className="text-gray-700 mb-4">
                Our Service integrates with third-party services that have their own privacy policies:
              </p>
              <ul className="list-disc pl-6 text-gray-700">
                <li><strong>Google OAuth:</strong> For authentication (Google Privacy Policy applies)</li>
                <li><strong>Mapbox:</strong> For geographic visualizations (Mapbox Privacy Policy applies)</li>
                <li><strong>OpenRouter:</strong> For AI-powered analysis (OpenRouter Privacy Policy applies)</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We encourage you to review the privacy policies of these third-party services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700">
                Our Service is not intended for children under the age of 13. We do not knowingly 
                collect personal information from children under 13. If we become aware that we have 
                collected personal information from a child under 13, we will take steps to delete 
                such information promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700">
                Your information may be transferred to and processed in countries other than your own. 
                We ensure that such transfers comply with applicable data protection laws and that 
                appropriate safeguards are in place to protect your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new Privacy Policy on this page and updating the 
                "Last updated" date. Your continued use of the Service after such changes constitutes 
                acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our privacy practices, 
                please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@producttrace.io<br />
                  <strong>Address:</strong> [Your Company Address]<br />
                  <strong>Phone:</strong> [Your Phone Number]
                </p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <Link 
                to="/" 
                className="text-blue-600 hover:text-blue-800 font-medium mb-4 sm:mb-0"
              >
                ← Back to ProductTrace
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-600 hover:text-gray-800"
              >
                View Terms of Service →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 